import { useEffect, useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Banner, Button, Card } from '../components/ui'
import { getMe, getPlaybook, savePlaybook } from '../api/endpoints'
import { siteMeta } from '../utils/site'
import type { Playbook, PlaybookField, SiteId } from '../types'

interface SectionDef {
  field: PlaybookField
  title: string
  prompt: string
  placeholder: string
}

// Mirrors PLAYBOOK_TITLES on the worker. Prompt + placeholder are the
// owner-facing interview script; if they answer the prompt, the bot
// inherits the answer immediately (no redeploy).
const SECTIONS: SectionDef[] = [
  {
    field: 'voice',
    title: 'Voice & tone',
    prompt:
      "If a brand-new visitor asked your favourite team member 'do you do X?', how would they reply? Show me the actual words — formal or casual, dry or warm, brief or thorough.",
    placeholder:
      'e.g. Friendly and concise. Devon-based, family-feel, but we know our stuff. Use "we" not "I". Avoid corporate-speak. Match the visitor\'s energy.',
  },
  {
    field: 'coreOffer',
    title: 'Core offer',
    prompt:
      'In one paragraph, how would you describe what you actually do — not the marketing copy, what a friend would say at a barbecue?',
    placeholder:
      'e.g. We build bespoke WordPress websites for small UK businesses. Typical project £1.5k–£15k. Clients are usually service businesses that have outgrown a DIY site.',
  },
  {
    field: 'notInScope',
    title: 'What we do NOT do',
    prompt:
      "What enquiries make you say internally 'we're not the right fit for this'?",
    placeholder:
      "e.g. No e-commerce platforms beyond basic. No projects under £800. We don't compete on the lowest-cost end. No adult/gambling/short-term-loan industries.",
  },
  {
    field: 'pricing',
    title: 'Pricing posture',
    prompt:
      "When a visitor asks 'how much?', what's the perfect bot reply? Range, defer entirely, or qualifying questions first?",
    placeholder:
      'e.g. Never quote a number. Acknowledge scope drives cost, mention typical range £1.5k–£15k, ask 1-2 qualifying questions, then offer to connect them with the team.',
  },
  {
    field: 'disqualifiers',
    title: 'Disqualifying signals',
    prompt:
      "What budget, timeline, or scope tells you immediately 'this isn't your client'?",
    placeholder:
      'e.g. Budget under £800 → suggest Wix/Squarespace. "Need it by Friday" → mention 4-6 weeks minimum. Magento/large e-commerce → refer elsewhere.',
  },
  {
    field: 'commonConfusions',
    title: 'Common visitor confusions',
    prompt: 'What do new visitors usually get wrong about your service?',
    placeholder:
      'e.g. They think hosting is included free forever. They confuse our starter package with a template DIY thing. They expect quotes by email same-day; we always discuss scope on a call first.',
  },
  {
    field: 'alwaysEscalate',
    title: 'Always escalate',
    prompt:
      'What questions should the bot NEVER attempt to answer, even if it thinks it knows?',
    placeholder:
      'e.g. Any specific quote. Existing-client billing. Technical questions about live client sites. Anyone asking about a previous developer or rumours — escalate immediately.',
  },
  {
    field: 'mustNeverPromise',
    title: 'Things the bot must never promise',
    prompt:
      'What guarantees, commitments, or claims would expose you to liability or damage trust if the bot made them on your behalf?',
    placeholder:
      'e.g. Never promise same-day callbacks. Never quote a fixed price. Never guarantee a delivery date. Never say "we always do X" or "we never charge for Y". Never claim qualifications, accreditations, or warranties — defer to the team.',
  },
  {
    field: 'proofPoints',
    title: 'Proof points / closers',
    prompt:
      'What story or fact, when it comes up naturally, tends to close a deal?',
    placeholder:
      'e.g. 25 years combined experience. Built sites for Scent from Devon, Wilson Leisure, Dartmouth Food Festival. Never lost a hosting client. Office in central Bath + Exeter.',
  },
  {
    field: 'examples',
    title: 'Example exchanges',
    prompt:
      "Give 3-5 conversations you'd be proud of — visitor question + how the team would ideally reply.",
    placeholder:
      "e.g.\nVisitor: Do you do SEO?\nTeam: We bake SEO best practices into every build, but we're not an SEO-only agency. If you're after ongoing rankings work, we'd partner with a specialist...",
  },
  {
    field: 'notes',
    title: 'Notes (capacity, holidays, recent changes)',
    prompt:
      'Anything seasonal, promotional, or recent that the bot should know — capacity, holidays, ongoing offers?',
    placeholder:
      "e.g. Closed for builds 23 Dec–6 Jan. Currently a 3-week wait before discovery calls. Just launched an audit-only engagement at £350.",
  },
]

export function PlaybookPage() {
  const queryClient = useQueryClient()
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe })
  const sites: SiteId[] = meQuery.data?.sites ?? []
  const [selectedSite, setSelectedSite] = useState<SiteId | null>(null)

  // Default the selector to the operator's first site once /me lands.
  useEffect(() => {
    if (!selectedSite && sites.length > 0) {
      setSelectedSite(sites[0])
    }
  }, [sites, selectedSite])

  if (meQuery.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (sites.length === 0) {
    return (
      <Banner variant="warn">
        No sites assigned to your account — ask an admin to grant access.
      </Banner>
    )
  }
  if (!selectedSite) return null

  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <h1 style={{ marginBottom: 6 }}>Playbook</h1>
        <p
          style={{
            color: 'var(--m-text-muted)',
            fontSize: 'var(--m-text-sm)',
          }}
        >
          Tribal knowledge that turns the bot into a real-business assistant.
          The website tells visitors what you do; this tells the bot how you
          sell, qualify, and disqualify. Saved instantly — the next chat
          message picks it up.
        </p>
      </div>

      {sites.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <select
            class="input"
            value={selectedSite}
            onChange={(e) =>
              setSelectedSite(
                (e.currentTarget as HTMLSelectElement).value as SiteId
              )
            }
            style={{ maxWidth: 320 }}
          >
            {sites.map((s) => (
              <option key={s} value={s}>
                {siteMeta(s).name}
              </option>
            ))}
          </select>
        </div>
      )}

      <PlaybookForm site={selectedSite} />
    </>
  )
}

function PlaybookForm({ site }: { site: SiteId }) {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['playbook', site],
    queryFn: () => getPlaybook(site),
  })

  const [draft, setDraft] = useState<Playbook>({})
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Hydrate draft from server data on first load + when site changes.
  useEffect(() => {
    if (data?.playbook) {
      setDraft(data.playbook)
      setSavedAt(null)
    }
  }, [data, site])

  const save = useMutation({
    mutationFn: () => savePlaybook(site, draft),
    onSuccess: (res) => {
      setDraft(res.playbook)
      setSavedAt(Date.now())
      queryClient.invalidateQueries({ queryKey: ['playbook', site] })
    },
  })

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (isError) {
    return (
      <Banner variant="error">
        Couldn’t load playbook. <a onClick={() => refetch()}>Retry</a>
      </Banner>
    )
  }

  // Detect whether draft differs from server-loaded state for the save
  // button's disabled state.
  const dirty = SECTIONS.some(
    (s) => (draft[s.field] ?? '') !== (data?.playbook?.[s.field] ?? '')
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (dirty) save.mutate()
      }}
    >
      {SECTIONS.map((s) => (
        <Card key={s.field} style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 4 }}>{s.title}</h3>
          <p
            style={{
              fontSize: 'var(--m-text-sm)',
              color: 'var(--m-text-muted)',
              marginBottom: 10,
            }}
          >
            {s.prompt}
          </p>
          <textarea
            class="textarea"
            rows={s.field === 'examples' ? 8 : 4}
            placeholder={s.placeholder}
            value={draft[s.field] ?? ''}
            onInput={(e) => {
              const v = (e.currentTarget as HTMLTextAreaElement).value
              setDraft((prev) => ({ ...prev, [s.field]: v }))
            }}
            maxLength={4000}
          />
        </Card>
      ))}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--m-surface, #fff)',
          padding: '12px 0',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <Button
          type="submit"
          variant="primary"
          loading={save.isPending}
          disabled={!dirty || save.isPending}
        >
          Save playbook
        </Button>
        {savedAt && !dirty && (
          <span
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-sm)',
            }}
          >
            Saved · live in the bot now
          </span>
        )}
        {save.isError && (
          <span
            style={{
              color: 'var(--m-danger, #c33)',
              fontSize: 'var(--m-text-sm)',
            }}
          >
            Save failed — try again
          </span>
        )}
      </div>
    </form>
  )
}
